import { getScrollTop, getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Laufband, das beim Scrollen schneller läuft.
 *
 * Der Entwurf gibt dem Band ein Grundtempo und addiert die geglättete
 * Scroll-Geschwindigkeit dazu: `30 + min(340, |v| * 0.55)` Pixel je Sekunde.
 * Beim Stillstand kriecht es, beim schnellen Scrollen zieht es an.
 *
 * Statt die Bewegung selbst zu rechnen — der Prototyp schiebt jede Zeile per
 * `translate3d` — wird hier nur die **Abspielgeschwindigkeit** von Horizons
 * vorhandener Marquee-Animation moduliert. Das erbt Verdopplung, Endlosschleife
 * und Richtungsumkehr, statt sie nachzubauen, und die Section bleibt
 * unangetastet.
 *
 * Der Faktor ist derselbe wie im Entwurf, nur als Verhältnis statt in Pixeln:
 *   `1 + min(11.33, |v| * 0.55 / 30)`
 *
 * ⚠️ Der Prototyp liest `window.scrollY`. Das ist hier ab 990 px konstant 0,
 * weil Horizon dann `.page-wrapper` scrollen lässt — die Beschleunigung
 * bliebe am Desktop einfach aus. Deshalb `getScrollTop()`, und aus demselben
 * Grund braucht der IntersectionObserver `getIntersectionRoot()`.
 */

const BASE_SPEED = 30;
const SCROLL_FACTOR = 0.55;
const MAX_ADDED = 340;
/** Glättung: Anteil, um den sich die Geschwindigkeit je Sekunde angleicht. */
const SMOOTHING = 6;

class MarqueeVelocity {
  #bands = [];
  #lastScrollTop = 0;
  #lastTime = 0;
  #velocity = 0;
  #frame = 0;
  #observer = null;

  start() {
    if (prefersReducedMotion()) return;

    const elements = document.querySelectorAll('marquee-component');
    if (elements.length === 0) return;

    this.#bands = Array.from(elements, (element) => ({ element, visible: false }));
    this.#lastScrollTop = getScrollTop();

    this.#observe();
    scrollContainerMediaQuery.addEventListener('change', () => this.#observe());

    this.#frame = requestAnimationFrame((time) => this.#tick(time));
  }

  /** Der Beobachtungsrahmen wechselt an der 990-px-Grenze mit dem Scrollport. */
  #observe() {
    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const band = this.#bands.find((b) => b.element === entry.target);
          if (band) band.visible = entry.isIntersecting;
        }
      },
      { root: getIntersectionRoot() }
    );
    for (const band of this.#bands) this.#observer.observe(band.element);
  }

  #tick(time) {
    // Beim ersten Frame gibt es noch keine Zeitbasis; 16 ms sind ein Frame.
    const delta = Math.min((time - this.#lastTime) / 1000, 0.05) || 0.016;
    this.#lastTime = time;

    const scrollTop = getScrollTop();
    const moved = scrollTop - this.#lastScrollTop;
    this.#lastScrollTop = scrollTop;

    const instant = moved / Math.max(delta, 0.001);
    this.#velocity += (instant - this.#velocity) * Math.min(1, delta * SMOOTHING);

    const added = Math.min(MAX_ADDED, Math.abs(this.#velocity) * SCROLL_FACTOR);
    const rate = (BASE_SPEED + added) / BASE_SPEED;

    if (!document.hidden) {
      for (const band of this.#bands) {
        if (!band.visible) continue;
        // ⚠️ Nicht `getAnimations()[0]` nehmen. Horizons Animation entsteht
        // erst, nachdem die Kopien gemessen sind — bis dahin ist die Liste
        // leer, und danach kann eine Transition davor stehen. Deshalb gezielt
        // nach dem Namen suchen und jeden Frame neu nachsehen, statt einmal zu
        // greifen und den Griff zu behalten.
        const wrapper = band.element.querySelector('.marquee__wrapper');
        const animation = wrapper
          ?.getAnimations()
          .find((candidate) => candidate.animationName === 'marquee-motion');
        if (animation) animation.playbackRate = rate;
      }
    }

    this.#frame = requestAnimationFrame((next) => this.#tick(next));
  }
}

new MarqueeVelocity().start();
